import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { compose } from 'redux';
import { Redirect } from 'react-router-dom';
import qs from 'query-string';

import { ErrorHandler, CenteredLoader, captureException } from '@playbookux/playbook-web-core';
import withLayout from '../../../core/components/with-layout';
import Tabs from '../../constants/tabs';

import AwaitingApproval from '../dashboard/components/awaiting-approval';

import {
  Container,
  ContainerButtons,
  Card,
  Title,
  PrimaryButton,
  SecondaryButton,
} from './styles';

import {
  CREATE_COMPANY_INVITE,
  GET_COMPANY_BY_LINK,
} from './graphql';

const REDUNDANT_INVITE_ERROR = 'Redundant invite';

const InviteUser = (props) => {
  const {
    history,
    isAuthenticated,
    match: {
      params: {
        inviteLink,
      },
    },
  } = props;

  const { awaitApproval } = qs.parse(window.location.search);
  const [mutationError, setMutationError] = useState(undefined);
  const [showAwaitScreen, setShowAwaitScreen] = useState(!!awaitApproval);

  useEffect(() => {
    if (!isAuthenticated) {
      history.push(`/sign-up?inviteLink=${inviteLink}`);
    }
  }, []);

  const [createCompanyInvite, {
    loading: mutationLoading,
  }] = useMutation(CREATE_COMPANY_INVITE);

  const {
    loading,
    error: queryError,
    data = {},
  } = useQuery(GET_COMPANY_BY_LINK, {
    variables: { inviteLink },
  });

  if (loading) {
    return (
      <Container>
        <CenteredLoader />
      </Container>
    );
  }

  const error = mutationError || queryError;

  // error handler
  if (error) {
    captureException('INVITE_USER_FAILED', error);
    if (error?.message === REDUNDANT_INVITE_ERROR && showAwaitScreen) {
      return (
        <AwaitingApproval />
      );
    }
    if (error?.message === REDUNDANT_INVITE_ERROR) {
      return <Redirect to="/" />;
    }
    return (
      <Container>
        <ErrorHandler
          error={error}
          defaultMessage="This is an invalid link"
        />
      </Container>
    );
  }

  if (showAwaitScreen) {
    return (
      <AwaitingApproval />
    );
  }

  const handleCreateCompanyInvite = async () => {
    try {
      await createCompanyInvite({
        variables: {
          inviteLink,
        },
      });
      setShowAwaitScreen(true);
    } catch (err) {
      setMutationError(err);
      captureException('HANDLE_CREATE_COMPANY_INVITE_FAILED', err);
    }
  };

  const handleCancelInvite = () => history.push('/create-workspace');

  const {
    companyByInviteLink: {
      name,
    },
  } = data;

  return (
    <Container>
      <Card>
        <Title as="h1">{`Do you want to request to join ${name} ?`}</Title>
        <ContainerButtons>
          <SecondaryButton onClick={handleCancelInvite}>No</SecondaryButton>
          <PrimaryButton
            onClick={handleCreateCompanyInvite}
            loading={mutationLoading}
            disabled={mutationLoading}
          >
            Yes
          </PrimaryButton>
        </ContainerButtons>
      </Card>
    </Container>
  );
};

InviteUser.propTypes = {
  isAuthenticated: PropTypes.bool,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      inviteLink: PropTypes.string,
    }),
  }).isRequired,
};

InviteUser.defaultProps = {
  isAuthenticated: false,
};

const mapDispatchToProps = { };

const mapStateToProps = ({ authentication }) => ({
  isAuthenticated: authentication.isAuthenticated,
});

const enhance = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLayout({ selectedTab: Tabs.PROJECT }),
);

export default enhance(InviteUser);
